import { CATEGORY_BY_ID } from '../categories';
import type { Registration } from '../registry';
import { ALL_PIN_OPTIONS as PINS } from '../pins';
import {
  DEFAULT_SPI_CS,
  DEFAULT_SPI_MISO,
  DEFAULT_SPI_MOSI,
  DEFAULT_SPI_SCK,
  DEFAULT_UART2_RX,
  DEFAULT_UART2_TX,
} from '../pins';

const c = CATEGORY_BY_ID.comms.colour;

function pinDefault(options: [string, string][], value: number): [string, string][] {
  return options.map(([label, v]) => [label === `GPIO ${value}` ? `${label} (default)` : label, v]);
}

const BAUD_OPTIONS: [string, string][] = [
  ['9600', '9600'],
  ['19200', '19200'],
  ['38400', '38400'],
  ['57600', '57600'],
  ['115200', '115200'],
  ['230400', '230400'],
  ['460800', '460800'],
  ['921600', '921600'],
];

export const commsRegistration: Registration = {
  definitions: [
    {
      type: 'esp32_i2c_begin',
      message0: 'I2C begin SDA %1 SCL %2 freq %3',
      args0: [
        { type: 'field_dropdown', name: 'SDA', options: pinDefault(PINS, 21) },
        { type: 'field_dropdown', name: 'SCL', options: pinDefault(PINS, 22) },
        { type: 'field_number', name: 'FREQ', value: 100000, min: 10000, max: 1000000, precision: 1000 },
      ],
      previousStatement: null,
      nextStatement: null,
      colour: c,
      tooltip: 'Start the I2C bus in master mode (Wire).',
    },
    {
      type: 'esp32_i2c_write_register',
      message0: 'I2C write device %1 register %2 value %3',
      args0: [
        { type: 'input_value', name: 'ADDR', check: 'Number' },
        { type: 'input_value', name: 'REG', check: 'Number' },
        { type: 'input_value', name: 'VALUE', check: 'Number' },
      ],
      previousStatement: null,
      nextStatement: null,
      colour: c,
      tooltip: 'Write one byte to a device register.',
    },
    {
      type: 'esp32_i2c_read_register',
      message0: 'I2C read device %1 register %2',
      args0: [
        { type: 'input_value', name: 'ADDR', check: 'Number' },
        { type: 'input_value', name: 'REG', check: 'Number' },
      ],
      output: 'Number',
      colour: c,
      tooltip: 'Read one byte from a device register (0 if the device does not answer).',
    },
    {
      type: 'esp32_i2c_scan',
      message0: 'I2C scan bus',
      previousStatement: null,
      nextStatement: null,
      colour: c,
      tooltip: 'Probe addresses 1-126 and print found devices to the serial monitor.',
    },
    {
      type: 'esp32_spi_begin',
      message0: 'SPI begin SCK %1 MISO %2 MOSI %3 CS %4',
      args0: [
        { type: 'field_dropdown', name: 'SCK', options: pinDefault(PINS, DEFAULT_SPI_SCK) },
        { type: 'field_dropdown', name: 'MISO', options: pinDefault(PINS, DEFAULT_SPI_MISO) },
        { type: 'field_dropdown', name: 'MOSI', options: pinDefault(PINS, DEFAULT_SPI_MOSI) },
        { type: 'field_dropdown', name: 'CS', options: pinDefault(PINS, DEFAULT_SPI_CS) },
      ],
      previousStatement: null,
      nextStatement: null,
      colour: c,
      tooltip: 'Start the SPI bus (default VSPI instance).',
    },
    {
      type: 'esp32_spi_transfer',
      message0: 'SPI transfer %1',
      args0: [{ type: 'input_value', name: 'DATA', check: 'Number' }],
      output: 'Number',
      colour: c,
      tooltip: 'Send one byte and return the byte received in the same exchange.',
    },
    {
      type: 'esp32_serial_begin',
      message0: 'serial begin at %1 baud',
      args0: [{ type: 'field_dropdown', name: 'BAUD', options: BAUD_OPTIONS }],
      previousStatement: null,
      nextStatement: null,
      colour: c,
      tooltip: 'Start USB/UART0 serial (Serial).',
    },
    {
      type: 'esp32_serial_print',
      message0: 'serial print %1 %2',
      args0: [
        { type: 'input_value', name: 'VALUE' },
        { type: 'field_checkbox', name: 'NEWLINE', checked: true },
      ],
      previousStatement: null,
      nextStatement: null,
      colour: c,
      tooltip: 'Print a value to the serial monitor (checkbox appends a newline).',
    },
    {
      type: 'esp32_serial_available',
      message0: 'serial data available?',
      output: 'Boolean',
      colour: c,
      tooltip: 'True when unread bytes are waiting in the serial receive buffer.',
    },
    {
      type: 'esp32_serial_read_line',
      message0: 'serial read line',
      output: 'String',
      colour: c,
      tooltip: 'Read one line (blocks until a newline arrives — pair with "serial data available?").',
    },
    {
      type: 'esp32_uart2_begin',
      message0: 'UART2 begin %1 baud RX %2 TX %3',
      args0: [
        { type: 'field_dropdown', name: 'BAUD', options: BAUD_OPTIONS },
        { type: 'field_dropdown', name: 'RX', options: pinDefault(PINS, DEFAULT_UART2_RX) },
        { type: 'field_dropdown', name: 'TX', options: pinDefault(PINS, DEFAULT_UART2_TX) },
      ],
      previousStatement: null,
      nextStatement: null,
      colour: c,
      tooltip: 'Start Serial2 on arbitrary pins.',
    },
    {
      type: 'esp32_uart2_print',
      message0: 'UART2 print %1 %2',
      args0: [
        { type: 'input_value', name: 'VALUE' },
        { type: 'field_checkbox', name: 'NEWLINE', checked: true },
      ],
      previousStatement: null,
      nextStatement: null,
      colour: c,
      tooltip: 'Print a value out of UART2.',
    },
    {
      type: 'esp32_uart2_available',
      message0: 'UART2 data available?',
      output: 'Boolean',
      colour: c,
      tooltip: 'True when UART2 has unread bytes.',
    },
    {
      type: 'esp32_uart2_read_byte',
      message0: 'UART2 read byte',
      output: 'Number',
      colour: c,
      tooltip: 'Read one byte from UART2.',
    },
  ],
  metas: [
    {
      type: 'esp32_i2c_begin',
      category: 'comms',
      label: 'I2C begin',
      description: 'Start the I2C bus: SDA pin, SCL pin, clock frequency in Hz.',
      fields: { SDA: { kind: 'dropdown', options: PINS.map((o) => o[1]) }, SCL: { kind: 'dropdown', options: PINS.map((o) => o[1]) }, FREQ: { kind: 'number' } },
    },
    {
      type: 'esp32_i2c_write_register',
      category: 'comms',
      label: 'I2C write register',
      description: 'Write one byte to a register on an I2C device.',
      fields: { ADDR: { kind: 'value', types: 'Number' }, REG: { kind: 'value', types: 'Number' }, VALUE: { kind: 'value', types: 'Number' } },
    },
    {
      type: 'esp32_i2c_read_register',
      category: 'comms',
      label: 'I2C read register',
      description: 'Value: one byte read from a device register.',
      fields: { ADDR: { kind: 'value', types: 'Number' }, REG: { kind: 'value', types: 'Number' } },
    },
    {
      type: 'esp32_i2c_scan',
      category: 'comms',
      label: 'I2C scan',
      description: 'Scan the I2C bus and print all discovered device addresses to Serial.',
      fields: {},
    },
    {
      type: 'esp32_spi_begin',
      category: 'comms',
      label: 'SPI begin',
      description: 'Start the SPI bus with SCK/MISO/MOSI/CS pins.',
      fields: { SCK: { kind: 'dropdown', options: PINS.map((o) => o[1]) }, MISO: { kind: 'dropdown', options: PINS.map((o) => o[1]) }, MOSI: { kind: 'dropdown', options: PINS.map((o) => o[1]) }, CS: { kind: 'dropdown', options: PINS.map((o) => o[1]) } },
    },
    {
      type: 'esp32_spi_transfer',
      category: 'comms',
      label: 'SPI transfer',
      description: 'Value: full-duplex exchange of one byte.',
      fields: { DATA: { kind: 'value', types: 'Number' } },
    },
    {
      type: 'esp32_serial_begin',
      category: 'comms',
      label: 'Serial begin',
      description: 'Start Serial at a chosen baud rate.',
      fields: { BAUD: { kind: 'dropdown', options: BAUD_OPTIONS.map((o) => o[1]) } },
    },
    {
      type: 'esp32_serial_print',
      category: 'comms',
      label: 'Serial print',
      description: 'Print any value to the serial monitor; NEWLINE appends a line break.',
      fields: { VALUE: { kind: 'value', types: 'any' }, NEWLINE: { kind: 'boolean' } },
    },
    {
      type: 'esp32_serial_available',
      category: 'comms',
      label: 'Serial available',
      description: 'Value: true when serial input bytes are waiting.',
      fields: {},
    },
    {
      type: 'esp32_serial_read_line',
      category: 'comms',
      label: 'Serial read line',
      description: 'Value: one line of serial input (blocks until newline).',
      fields: {},
    },
    {
      type: 'esp32_uart2_begin',
      category: 'comms',
      label: 'UART2 begin',
      description: 'Start Serial2 at a baud rate on chosen RX/TX pins.',
      fields: { BAUD: { kind: 'dropdown', options: BAUD_OPTIONS.map((o) => o[1]) }, RX: { kind: 'dropdown', options: PINS.map((o) => o[1]) }, TX: { kind: 'dropdown', options: PINS.map((o) => o[1]) } },
    },
    {
      type: 'esp32_uart2_print',
      category: 'comms',
      label: 'UART2 print',
      description: 'Print a value out of UART2.',
      fields: { VALUE: { kind: 'value', types: 'any' }, NEWLINE: { kind: 'boolean' } },
    },
    {
      type: 'esp32_uart2_available',
      category: 'comms',
      label: 'UART2 available',
      description: 'Value: true when UART2 input bytes are waiting.',
      fields: {},
    },
    {
      type: 'esp32_uart2_read_byte',
      category: 'comms',
      label: 'UART2 read byte',
      description: 'Value: one byte from UART2.',
      fields: {},
    },
  ],
};
